// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — bootstrap and screens.
 *
 * Screen graph: menu -> (solo | room entry -> lobby) -> round -> results -> ...
 *
 * Two structural rules run through this file, both of them scar tissue from
 * other games in this factory:
 *
 *  - ONE ROOM PER SESSION. The Net is created once when a room is entered and
 *    lives until the player goes back to the menu. A rematch is a new round
 *    number inside the SAME room, never a leave-and-rejoin.
 *  - A NET HANDLER MUST NOT NAVIGATE. onPeers/onHostChange fire in the lobby,
 *    mid-round, and just after a round ends. They repaint whatever screen is
 *    current; they never decide which screen that is. A `showMenu()` fallback in
 *    one of these once ejected both players from a working game.
 */

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  mintCode,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { newSeed } from '@ben-gy/game-engine/rng';

import { createCountdown, type Countdown } from './countdown';
import { PALETTE, seatColour, seatGlyph } from './palette';
import { DEFAULT_MODE, MODES, modeOf, type Mode } from './modes';
import { greedyMazeLengthCached } from './field';
import { CREEPS, Game, TOWERS, TOWER_KINDS, sellValue, sendCost, type TowerKind } from './game';
import { Match, type Standing } from './match';
import { Session, type PeerStatus } from './session';
import { Renderer } from './render';
import { el, esc, on, openModal, toast } from './ui';

const SLUG = 'sporeline';
const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);

/** Sim tick. An interval, not rAF: a backgrounded tab must keep its field
 *  running, because in a live room the host's wave clock does not pause. */
const TICK_MS = 50;

const app = document.querySelector<HTMLElement>('#app')!;

// ── app shell ───────────────────────────────────────────────────────────────

app.innerHTML = `
  <div class="main-content"><div id="screen"></div></div>
  <footer class="site-footer">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
  </footer>`;

const screen = document.querySelector<HTMLElement>('#screen')!;

// ── session-wide state ──────────────────────────────────────────────────────

let mode: Mode = modeOf(store.get<string>('mode', DEFAULT_MODE.id));
let playerName = store.get<string>('name', '') || `Player ${Math.floor(Math.random() * 900 + 100)}`;

let net: Net | null = null;
let rounds: Rounds | null = null;
let roomCode = '';
/** Match tally across rounds in this room — seat id -> rounds won. */
const tally = new Map<PeerId, number>();

let match: Match | null = null;
let session: Session | null = null;
let renderer: Renderer | null = null;
let countdown: Countdown | null = null;
let tick: ReturnType<typeof setInterval> | null = null;
let raf = 0;
let paused = false;
let armed = false;

/** The tray chip currently armed. The SELECTED TOWER lives on the renderer,
 *  because the range ring is the only thing that reads it. */
let selectedKind: TowerKind | null = null;

const reduceMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

hardenViewport();

// ── helpers ─────────────────────────────────────────────────────────────────

const playing = (on_: boolean): void => {
  document.body.classList.toggle('playing', on_);
};

/** The Game this peer is actually playing, whichever mode we are in. */
function myGame(): Game | null {
  if (session) return session.game;
  if (match) return match.games[0];
  return null;
}

function teardownRound(): void {
  countdown?.cancel();
  countdown = null;
  if (tick) clearInterval(tick);
  tick = null;
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  session?.destroy();
  session = null;
  match = null;
  renderer = null;
  selectedKind = null;
  paused = false;
  armed = false;
  playing(false);
}

/** Leave the room entirely. The ONLY place net.leave() is ever called. */
async function leaveRoom(): Promise<void> {
  teardownRound();
  rounds?.destroy();
  rounds = null;
  const n = net;
  net = null;
  roomCode = '';
  tally.clear();
  clearRoomInUrl();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* already gone */
    }
  }
}

// ── menu ────────────────────────────────────────────────────────────────────

function showMenu(): void {
  teardownRound();
  screen.innerHTML = `
    <div class="menu">
      <h1 class="title">Sporeline</h1>
      <p class="tagline">Your towers <em>are</em> the walls. Fold the road longer, kill more &mdash; then spend the gold sending spores into a rival's field.</p>

      <div class="modes" role="radiogroup" aria-label="Mode">
        ${MODES.map(
          (m) => `
          <button class="mode-card${m.id === mode.id ? ' on' : ''}" role="radio"
                  aria-checked="${m.id === mode.id}" data-mode="${m.id}" type="button">
            <span class="mode-name">${esc(m.name)}</span>
            <span class="mode-blurb">${esc(m.blurb)}</span>
          </button>`,
        ).join('')}
      </div>

      <div class="menu-actions">
        <button class="btn btn-primary" id="play-solo" type="button">Play</button>
        <button class="btn" id="play-friends" type="button">Play with friends</button>
      </div>

      <div class="menu-links">
        <button class="btn btn-quiet" id="how" type="button">How to play</button>
        <button class="btn btn-quiet" id="about" type="button">About</button>
        <button class="btn btn-quiet" id="mute" type="button" aria-pressed="${sfx.muted()}">
          ${sfx.muted() ? 'Sound off' : 'Sound on'}
        </button>
      </div>

      <p class="best" id="best"></p>
    </div>`;

  paintBest();

  screen.querySelectorAll<HTMLButtonElement>('.mode-card').forEach((card) => {
    card.addEventListener('click', () => {
      mode = modeOf(card.dataset.mode);
      store.set('mode', mode.id);
      sfx.unlock();
      sfx.play('select');
      showMenu();
    });
  });

  screen.querySelector('#play-solo')?.addEventListener('click', () => {
    sfx.unlock();
    startSolo();
  });
  screen.querySelector('#play-friends')?.addEventListener('click', () => {
    sfx.unlock();
    showRoomEntry();
  });
  screen.querySelector('#how')?.addEventListener('click', showHowTo);
  screen.querySelector('#about')?.addEventListener('click', showAbout);
  screen.querySelector('#mute')?.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    showHowTo();
  }
}

function paintBest(): void {
  const node = screen.querySelector<HTMLElement>('#best');
  if (!node) return;
  const best = store.get<number>(`best:${mode.id}`, 0) ?? 0;
  node.textContent = best > 0 ? `Best on ${mode.name}: wave ${best}` : '';
}

function showHowTo(): void {
  openModal(
    'How to play',
    `<p><strong>Your towers are the walls.</strong> Creeps walk from the Gate to your Heart along
      whatever road is open &mdash; so drop towers to fold that road back on itself. A longer road
      means more seconds inside your guns.</p>
     <p><strong>You can never seal the Gate.</strong> A placement that would block every route is
      rejected, so there is always one way through. Find the longest one.</p>
     <p><strong>Kills pay gold.</strong> Spend it on more towers, on upgrades &mdash; or on
      <em>sending a spore</em> into a rival's field, which drops a creep on them <em>and</em> raises
      your income for the rest of the match.</p>
     <p><strong>Last Heart beating wins.</strong></p>
     <h3 class="modal-sub">Your three towers</h3>
     ${towerReferenceHtml()}
     <p class="muted">Drag a tower from the tray onto a cell &mdash; the glowing line previews the
      road you would create, with a <span class="good">+7</span> badge for the cells you gain. Tap a
      placed tower to upgrade or sell it. On a keyboard: <kbd>1</kbd><kbd>2</kbd><kbd>3</kbd> pick a
      tower, <kbd>Esc</kbd> deselects, <kbd>Space</kbd> pauses.</p>`,
  );
}

/**
 * The three towers, their glyph, price and what they are actually for. Built
 * from TOWERS rather than written out, so a spec change can never leave the
 * explanation lying — the `hint` on each spec had until now only ever reached
 * screen readers, via the tray's aria-label.
 */
function towerReferenceHtml(): string {
  return `<ul class="tower-ref">
    ${TOWER_KINDS.map((kind) => {
      const t = TOWERS[kind];
      // Mire deals no damage at all, so quoting "0 damage" reads as broken
      // rather than as the point of it.
      const punch =
        t.damage[0] === 0
          ? `slows to ${Math.round(t.slow[0] * 100)}%`
          : `${t.damage[0]} damage`;
      return `<li>
        <span class="chip-glyph chip-${t.glyph}" style="--c:${towerColour(kind)}"></span>
        <div>
          <strong>${esc(t.name)}</strong> <span class="muted">&mdash; ${t.cost} gold</span>
          <div class="tower-ref-hint">${esc(t.hint)}</div>
          <div class="muted tower-ref-stat">${punch} &middot; ${t.range[0]} cell reach &middot; upgrades twice</div>
        </div>
      </li>`;
    }).join('')}
  </ul>`;
}

function showAbout(): void {
  openModal(
    'About Sporeline',
    `<p>An open-field maze defence: there is no fixed path, and every tower you place is also a wall.
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      &mdash; <a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>.</p>
     <p>No cookies, no accounts, no tracking beyond anonymous, cookie-less page counts.</p>
     <p><strong>Playing with friends</strong> connects your browsers directly to each other
      (peer-to-peer WebRTC). A public signalling relay is used only to introduce the browsers; no
      game state is ever stored on a server, and there is no game server at all. Connecting shares
      your IP address with the people in your room, as any direct connection does.</p>
     <p class="muted">All art and audio are generated in the browser. Open source, MIT.</p>`,
  );
}

// ── solo ────────────────────────────────────────────────────────────────────

function startSolo(): void {
  teardownRound();
  const seed = newSeed();
  match = new Match({
    mode,
    seed,
    humanSeat: 0,
    seats: [
      { name: 'You', policy: null },
      { name: 'Sporebot', policy: 'balanced' },
      { name: 'Creeper', policy: 'pressure' },
    ],
  });
  paintGameScreen();
  beginCountdown();
}

// ── room + lobby ────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  teardownRound();
  const url = new URL(location.href);
  const deep = url.searchParams.get('room');
  if (deep) {
    // A deep-linked invite is honoured ONCE. It is cleared immediately so a
    // reload, or reopening from a home-screen icon, does not drag the player
    // back into a room they have already left.
    clearRoomInUrl();
    void enterRoom(normalizeRoomCode(deep), false);
    return;
  }

  createRoomEntry({
    container: screen,
    title: 'Play with friends',
    subtitle: 'Start a new room, or enter a code to join a friend.',
    onSubmit: (code, created) => void enterRoom(normalizeRoomCode(code) || mintCode(), created),
    onCancel: showMenu,
  });
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  screen.innerHTML = `<div class="menu"><p class="connecting"><span class="spinner"></span> Connecting…</p></div>`;

  // Must run before the FIRST mesh on the page: trystero builds one global
  // connection pool from the first joinRoom's config.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail-open: STUN only */
  }

  roomCode = code;
  setRoomInUrl(code);

  net = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      // These repaint. They never navigate — see the header note.
      onHostChange: (_id, isSelf) => {
        session?.onHostChange(isSelf);
        repaintRoomScreen();
      },
      onPeerLeave: (id) => {
        session?.onPeerLeave(id);
        repaintRoomScreen();
      },
      onPeers: () => repaintRoomScreen(),
    },
  );

  rounds = createRounds({
    net,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: mode.id }),
    onRound: (info) => startNetRound(info),
    onChange: () => repaintRoomScreen(),
  });

  showLobby();
}

function showLobby(): void {
  teardownRound();
  if (!net || !rounds) return showMenu();
  screen.innerHTML = '';

  const picker = el('div', 'lobby-mode');
  picker.innerHTML = `<div class="lobby-mode-inner"></div>`;
  screen.appendChild(picker);
  paintModePicker(picker.firstElementChild as HTMLElement);

  const lobbyHost = el('div', 'lobby-host');
  screen.appendChild(lobbyHost);

  createLobby({
    container: lobbyHost,
    net,
    rounds,
    roomCode,
    minPlayers: 2,
    maxPlayers: 4,
    onCancel: () => void leaveRoom().then(showMenu),
  });
}

/**
 * The mode picker inside the lobby.
 *
 * The HOST's pick is what the room plays, and it travels frozen inside the round
 * start. A guest sees the host's GOSSIPED choice (`state().hostOpts`), never its
 * own — rendering a local pick and labelling it the host's is a confident lie,
 * and Sporeline's modes change the field dimensions, so two peers disagreeing
 * would be playing different games on the same seed.
 */
function paintModePicker(node: HTMLElement): void {
  if (!net || !rounds) return;
  const state = rounds.state();
  const hostMode = modeOf((state.hostOpts as { mode?: string } | null)?.mode);
  const amHost = net.isHost() && net.hostSettled();

  if (!amHost) {
    node.innerHTML = `<p class="lobby-mode-label">Host has chosen
      <strong>${esc(state.hostOpts ? hostMode.name : '…')}</strong>
      <span class="muted">${esc(state.hostOpts ? hostMode.blurb : 'waiting for the host')}</span></p>`;
    return;
  }

  node.innerHTML = `<p class="lobby-mode-label">Mode (your pick — everyone plays it)</p>
    <div class="modes modes-compact" role="radiogroup" aria-label="Mode">
      ${MODES.map(
        (m) => `<button class="mode-card${m.id === mode.id ? ' on' : ''}" role="radio"
                 aria-checked="${m.id === mode.id}" data-mode="${m.id}" type="button">
                 <span class="mode-name">${esc(m.name)}</span>
                 <span class="mode-blurb">${esc(m.blurb)}</span></button>`,
      ).join('')}
    </div>`;
  node.querySelectorAll<HTMLButtonElement>('.mode-card').forEach((card) => {
    card.addEventListener('click', () => {
      mode = modeOf(card.dataset.mode);
      store.set('mode', mode.id);
      sfx.play('select');
      repaintRoomScreen();
    });
  });
}

/** Repaint whatever room screen is showing. Never decides WHICH screen. */
function repaintRoomScreen(): void {
  const picker = screen.querySelector<HTMLElement>('.lobby-mode-inner');
  if (picker) paintModePicker(picker);
  if (session) paintHud();
  const results = screen.querySelector<HTMLElement>('.results');
  if (results && session) paintResults(session.standings(), session.winner);
}

function startNetRound(info: RoundInfo): void {
  teardownRound();
  if (!net) return;

  const opts = info.opts as { mode?: string } | undefined;
  mode = modeOf(opts?.mode);

  if (!info.seated) {
    // The round started without us — spectate rather than dead-screen.
    screen.innerHTML = `
      <div class="menu">
        <h2 class="title-sm">Round in progress</h2>
        <p class="tagline">You're in the next one. Ready up and it'll start when this round ends.</p>
        <button class="btn" id="to-lobby" type="button">Back to lobby</button>
      </div>`;
    screen.querySelector('#to-lobby')?.addEventListener('click', showLobby);
    return;
  }

  session = new Session({
    net,
    mode,
    seed: info.seed,
    players: info.players,
    selfId: net.selfId,
    onChange: () => paintHud(),
    onEnd: (winner) => endRound(winner),
    onSporeIn: () => {
      sfx.play('hit');
      renderer?.kick(6);
      toast('A spore landed on your field', 'bad');
    },
    onWave: (w) => {
      sfx.play('powerup');
      toast(`Wave ${w}`);
    },
  });

  paintGameScreen();
  beginCountdown();
}

// ── the game screen ─────────────────────────────────────────────────────────

function paintGameScreen(): void {
  const g = myGame();
  if (!g) return;
  playing(true);

  screen.innerHTML = `
    <div class="game">
      <header class="hud">
        <div class="hud-stats">
          <span class="hud-stat" id="s-gold" title="Gold"></span>
          <span class="hud-stat" id="s-heart" title="Heart"></span>
          <span class="hud-stat" id="s-wave" title="Wave"></span>
        </div>
        <div class="hud-buttons">
          <button class="icon-btn" id="btn-pause" type="button" aria-label="Pause">II</button>
          <button class="icon-btn" id="btn-quit" type="button" aria-label="Leave">X</button>
        </div>
      </header>
      <div class="wave-bar"><div class="wave-bar-fill" id="wave-fill"></div></div>
      <div class="rivals" id="rivals"></div>
      <div class="field-wrap"><canvas id="field" aria-label="Your field"></canvas></div>
      <div class="tray" id="tray"></div>
      <div class="sheet" id="sheet" hidden></div>
      <div class="pause-overlay" id="pause-overlay" hidden>
        <div class="pause-card">
          <h2>Paused</h2>
          <button class="btn btn-primary" id="resume" type="button">Resume</button>
          <button class="btn" id="restart" type="button">Restart</button>
          <button class="btn btn-quiet" id="help-mid" type="button">How to play</button>
        </div>
      </div>
    </div>`;

  const canvas = screen.querySelector<HTMLCanvasElement>('#field')!;
  renderer = new Renderer(canvas);
  // Lay the board out NOW rather than waiting for the first animation frame —
  // input geometry must not depend on a frame having rendered. See
  // Renderer.cellAt. A 0-size measurement here is harmless: cellAt re-measures.
  renderer.resize(g.spec);

  paintTray();
  paintRivals();
  paintHud();
  wireInput(canvas);

  screen.querySelector('#btn-pause')?.addEventListener('click', () => setPaused(!paused));
  screen.querySelector('#resume')?.addEventListener('click', () => setPaused(false));
  screen.querySelector('#restart')?.addEventListener('click', () => {
    if (session) return; // a live room restarts via the rematch vote, not here
    setPaused(false);
    startSolo();
  });
  screen.querySelector('#help-mid')?.addEventListener('click', showHowTo);
  screen.querySelector('#btn-quit')?.addEventListener('click', () => {
    if (session) void leaveRoom().then(showMenu);
    else showMenu();
  });

  startLoop();
}

function paintTray(): void {
  const tray = screen.querySelector<HTMLElement>('#tray');
  const g = myGame();
  if (!tray || !g) return;

  tray.innerHTML = TOWER_KINDS.map((kind) => {
    const t = TOWERS[kind];
    return `<button class="chip" data-kind="${kind}" type="button"
              aria-label="${esc(t.name)}, ${t.cost} gold. ${esc(t.hint)}">
        <span class="chip-glyph chip-${t.glyph}" style="--c:${towerColour(kind)}"></span>
        <span class="chip-name">${esc(t.name)}</span>
        <span class="chip-cost">${t.cost}</span>
      </button>`;
  }).join('');

  tray.querySelectorAll<HTMLButtonElement>('.chip').forEach((chip) => {
    const kind = chip.dataset.kind as TowerKind;
    // Tap selects; the canvas then places. Dragging is handled in wireInput so
    // one pointer stream drives both idioms — see principle #19.
    chip.addEventListener('click', () => {
      selectedKind = selectedKind === kind ? null : kind;
      hideSheet();
      sfx.unlock();
      sfx.play('blip');
      paintTraySelection();
    });
  });
  paintTraySelection();
}

function towerColour(kind: TowerKind): string {
  return kind === 'spitter'
    ? PALETTE.towerSpitter
    : kind === 'mire'
      ? PALETTE.towerMire
      : PALETTE.towerLance;
}

function paintTraySelection(): void {
  const g = myGame();
  screen.querySelectorAll<HTMLButtonElement>('.chip').forEach((chip) => {
    const kind = chip.dataset.kind as TowerKind;
    chip.classList.toggle('on', selectedKind === kind);
    const afford = !!g && g.gold >= TOWERS[kind].cost;
    chip.classList.toggle('poor', !afford);
    chip.setAttribute('aria-pressed', String(selectedKind === kind));
  });
}

/** Rival mini-fields — display only, from the `fld` channel. */
function paintRivals(): void {
  const strip = screen.querySelector<HTMLElement>('#rivals');
  if (!strip) return;

  const rows: { seat: number; name: string; heart: number; max: number; alive: boolean }[] = [];
  if (session) {
    for (const s of session.all()) {
      if (s.seat === session.selfSeat) continue;
      rows.push({ seat: s.seat, name: s.name, heart: s.heart, max: s.maxHeart, alive: s.alive });
    }
  } else if (match) {
    for (let i = 1; i < match.games.length; i++) {
      rows.push({
        seat: i,
        name: match.seats[i].name,
        heart: match.games[i].heart,
        max: mode.heartBeats,
        alive: !match.games[i].over,
      });
    }
  }

  if (!rows.length) {
    strip.innerHTML = '';
    strip.hidden = true;
    return;
  }
  strip.hidden = false;
  strip.innerHTML = rows
    .map(
      (r) => `
      <button class="rival${r.alive ? '' : ' dead'}" data-seat="${r.seat}" type="button"
              aria-label="Send a spore at ${esc(r.name)}">
        <span class="rival-glyph glyph-${seatGlyph(r.seat)}" style="--c:${seatColour(r.seat)}"></span>
        <span class="rival-name">${esc(r.name)}</span>
        <span class="rival-heart">${r.alive ? `${r.heart}/${r.max}` : 'out'}</span>
      </button>`,
    )
    .join('');

  strip.querySelectorAll<HTMLButtonElement>('.rival').forEach((btn) => {
    btn.addEventListener('click', () => trySend(Number(btn.dataset.seat)));
  });
}

function trySend(seat: number): void {
  const g = myGame();
  if (!g || g.over) return;
  const cost = sendCost(g.stats.sent);
  if (g.gold < cost) {
    sfx.play('lose');
    toast(`A spore costs ${cost} gold`, 'bad');
    return;
  }

  if (session) {
    if (!session.spore(seat)) return;
  } else if (match) {
    if (g.spendSend() === 0) return;
    match.send(0, seat);
  }
  sfx.play('powerup');
  toast(`Spore sent — your income is now higher`);
  paintHud();
}

function paintHud(): void {
  const g = myGame();
  if (!g) return;
  const gold = screen.querySelector<HTMLElement>('#s-gold');
  const heart = screen.querySelector<HTMLElement>('#s-heart');
  const wave = screen.querySelector<HTMLElement>('#s-wave');
  const fill = screen.querySelector<HTMLElement>('#wave-fill');

  const waveNo = session ? session.wave : (match?.wave ?? 0);
  if (gold) gold.textContent = `${Math.floor(g.gold)}g`;
  if (heart) {
    heart.textContent = `${g.heart}/${mode.heartBeats}`;
    heart.classList.toggle('low', g.heart <= mode.heartBeats * 0.34);
  }
  if (wave) wave.textContent = `Wave ${waveNo}`;
  if (fill) {
    const elapsed = session ? 0 : (match?.waveMs ?? 0);
    // In a room the wave clock is the host's, so the local bar tracks the
    // field's own wave timer rather than pretending to know the host's.
    const ms = session ? g.waveMs : elapsed;
    fill.style.width = `${Math.max(0, Math.min(100, (ms / mode.waveMs) * 100))}%`;
  }
  paintTraySelection();
  paintRivals();
}

// ── input ───────────────────────────────────────────────────────────────────

/**
 * One pointer stream, both idioms.
 *
 * Tap a tray chip then tap a cell, OR drag a chip straight onto a cell — both
 * are first-class, which is what principle #19 asks of a board-game control
 * scheme. Everything is Pointer Events with `setPointerCapture`, positions come
 * from `clientX/Y - getBoundingClientRect()` (never `offsetX/Y`), and
 * `pointercancel` aborts exactly like a release outside the field.
 */
function wireInput(canvas: HTMLCanvasElement): void {
  const offs: (() => void)[] = [];
  let dragKind: TowerKind | null = null;
  let pointerId: number | null = null;

  const updatePreview = (clientX: number, clientY: number, kind: TowerKind): void => {
    const g = myGame();
    if (!g || !renderer) return;
    const cell = renderer.cellAt(g.spec, clientX, clientY);
    if (cell < 0) {
      renderer.preview = null;
      return;
    }
    const probe = g.probe(cell, kind);
    renderer.preview = {
      cell,
      kind,
      ok: probe.ok,
      road: probe.road,
      before: g.roadLength(),
    };
  };

  // ── dragging from the tray ────────────────────────────────────────────────
  screen.querySelectorAll<HTMLButtonElement>('.chip').forEach((chip) => {
    const kind = chip.dataset.kind as TowerKind;
    offs.push(
      on(chip, 'pointerdown', (e) => {
        if (pointerId !== null) return;
        pointerId = e.pointerId;
        dragKind = kind;
        chip.setPointerCapture(e.pointerId);
      }),
      on(chip, 'pointermove', (e) => {
        if (e.pointerId !== pointerId || !dragKind) return;
        updatePreview(e.clientX, e.clientY, dragKind);
      }),
      on(chip, 'pointerup', (e) => {
        if (e.pointerId !== pointerId) return;
        const kindNow = dragKind;
        pointerId = null;
        dragKind = null;
        const preview = renderer?.preview ?? null;
        if (renderer) renderer.preview = null;
        // A release over the field is a placement; a release on the chip is a
        // tap, and the chip's own click handler does the selecting.
        if (kindNow && preview) tryPlace(preview.cell, kindNow);
      }),
      on(chip, 'pointercancel', () => {
        pointerId = null;
        dragKind = null;
        if (renderer) renderer.preview = null;
      }),
    );
  });

  // ── tapping the field ─────────────────────────────────────────────────────
  offs.push(
    on(canvas, 'pointerdown', (e) => {
      const g = myGame();
      if (!g || !renderer || paused) return;
      canvas.setPointerCapture(e.pointerId);
      const cell = renderer.cellAt(g.spec, e.clientX, e.clientY);
      if (cell < 0) return;

      if (g.towers.has(cell)) {
        selectedKind = null;
        renderer.selected = cell;
        paintTraySelection();
        showSheet(cell);
        sfx.play('blip');
        return;
      }
      hideSheet();
      renderer.selected = null;
      if (selectedKind) tryPlace(cell, selectedKind);
    }),
    on(canvas, 'pointermove', (e) => {
      if (!selectedKind || !renderer || paused) return;
      updatePreview(e.clientX, e.clientY, selectedKind);
    }),
    on(canvas, 'pointerleave', () => {
      if (renderer) renderer.preview = null;
    }),
  );

  // ── keyboard ──────────────────────────────────────────────────────────────
  offs.push(
    on(document, 'keydown', (e) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === '1' || e.key === '2' || e.key === '3') {
        selectedKind = TOWER_KINDS[Number(e.key) - 1];
        paintTraySelection();
      } else if (e.key === 'Escape') {
        selectedKind = null;
        if (renderer) renderer.selected = null;
        hideSheet();
        paintTraySelection();
      } else if (e.key === ' ') {
        e.preventDefault();
        setPaused(!paused);
      }
    }),
  );

  (canvas as unknown as { _offs?: (() => void)[] })._offs = offs;
}

function tryPlace(cell: number, kind: TowerKind): void {
  const g = myGame();
  if (!g || g.over || paused) return;
  const before = g.roadLength();
  const ok = g.place(cell, kind);
  if (!ok) {
    const last = g.events[g.events.length - 1];
    const why =
      last?.t === 'reject' && last.reason === 'seals'
        ? 'That would seal the Gate'
        : last?.t === 'reject' && last.reason === 'gold'
          ? 'Not enough gold'
          : last?.t === 'reject' && last.reason === 'creep'
            ? 'A creep is standing there'
            : 'You cannot build there';
    sfx.play('lose');
    toast(why, 'bad');
    renderer?.kick(3);
    return;
  }
  const after = g.roadLength();
  sfx.play('select');
  if (renderer) {
    const [x, y] = renderer.cellCentre(g.spec, cell);
    renderer.burst(x, y, towerColour(kind), 8, 50);
  }
  if (after > before) toast(`Road +${after - before}`);
  paintHud();
}

function showSheet(cell: number): void {
  const g = myGame();
  const sheet = screen.querySelector<HTMLElement>('#sheet');
  if (!g || !sheet) return;
  const tower = g.towers.get(cell);
  if (!tower) return hideSheet();

  const spec = TOWERS[tower.kind];
  const canUpgrade = tower.level < 2;
  const cost = canUpgrade ? spec.upgrade[tower.level] : 0;

  sheet.hidden = false;
  sheet.innerHTML = `
    <div class="sheet-info">
      <strong>${esc(spec.name)}</strong>
      <span class="muted">Level ${tower.level + 1}${
        spec.damage[tower.level] > 0
          ? ` · ${spec.damage[tower.level]} dmg`
          : ` · slows ${Math.round((1 - spec.slow[tower.level]) * 100)}%`
      } · range ${spec.range[tower.level]}</span>
    </div>
    <div class="sheet-actions">
      ${
        canUpgrade
          ? `<button class="btn btn-primary" id="do-up" type="button">Upgrade ${cost}g</button>`
          : `<span class="muted">Fully upgraded</span>`
      }
      <button class="btn" id="do-sell" type="button">Sell ${sellValue(tower)}g</button>
      <button class="btn btn-quiet" id="do-close" type="button">Close</button>
    </div>`;

  sheet.querySelector('#do-up')?.addEventListener('click', () => {
    if (g.upgrade(cell)) {
      sfx.play('powerup');
      showSheet(cell);
      paintHud();
    } else {
      sfx.play('lose');
      toast('Not enough gold', 'bad');
    }
  });
  sheet.querySelector('#do-sell')?.addEventListener('click', () => {
    g.sell(cell);
    sfx.play('coin');
    hideSheet();
    if (renderer) renderer.selected = null;
    paintHud();
  });
  sheet.querySelector('#do-close')?.addEventListener('click', () => {
    hideSheet();
    if (renderer) renderer.selected = null;
  });
}

function hideSheet(): void {
  const sheet = screen.querySelector<HTMLElement>('#sheet');
  if (sheet) {
    sheet.hidden = true;
    sheet.innerHTML = '';
  }
}

function setPaused(next: boolean): void {
  // A live room has no pause — the host's wave clock does not stop for one peer,
  // so offering a pause would be a lie that costs the player a wave.
  if (session) {
    if (next) toast('No pausing in a live room');
    return;
  }
  paused = next;
  const overlay = screen.querySelector<HTMLElement>('#pause-overlay');
  if (overlay) overlay.hidden = !paused;
}

// ── the loop ────────────────────────────────────────────────────────────────

function beginCountdown(): void {
  armed = false;
  const host = screen.querySelector<HTMLElement>('.field-wrap') ?? screen;
  countdown = createCountdown({
    root: host,
    sfx,
    reducedMotion: reduceMotion,
    onDone: () => {
      armed = true;
      countdown = null;
    },
  });
}

function startLoop(): void {
  if (tick) clearInterval(tick);

  // The SIM runs on an interval so a backgrounded tab keeps its field alive —
  // in a live room the host's wave clock does not pause, and a frozen field
  // would wake up to a wall of creeps it never got to shoot at.
  tick = setInterval(() => {
    if (paused || !armed) return;
    if (session) session.step(TICK_MS);
    else if (match) match.step(TICK_MS);
    drainEvents();
    if (match && match.over) endRound(match.winner);
  }, TICK_MS);

  let last = performance.now();
  const frame = (now: number): void => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const g = myGame();
    if (g && renderer) renderer.draw(g, paused ? 0 : dt, now);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
}

/** Turn the sim's event stream into sound and particles. */
function drainEvents(): void {
  const g = myGame();
  if (!g || !renderer) return;
  const events = g.events;
  if (!events.length) return;
  g.events = [];

  let dirty = false;
  for (const ev of events) {
    if (ev.t === 'kill') {
      const [x, y] = renderer.cellCentre(g.spec, ev.cell);
      renderer.burst(x, y, PALETTE.creepSpore, 7, 55);
      sfx.play('coin');
      dirty = true;
    } else if (ev.t === 'leak') {
      const [x, y] = renderer.cellCentre(g.spec, g.spec.heart);
      renderer.burst(x, y, PALETTE.heart, 16, 90);
      renderer.kick(g.heart <= 3 ? 12 : 6);
      sfx.play('hit');
      dirty = true;
    } else if (ev.t === 'dead') {
      sfx.play('lose');
      renderer.kick(18);
    } else if (ev.t === 'wave') {
      dirty = true;
    }
  }
  if (dirty) paintHud();
}

// ── results ─────────────────────────────────────────────────────────────────

function endRound(winner: number): void {
  if (tick) clearInterval(tick);
  tick = null;
  playing(false);
  countdown?.cancel();
  countdown = null;

  if (session) {
    const id = session.players[winner]?.id;
    if (id) tally.set(id, (tally.get(id) ?? 0) + 1);
    rounds?.finish();
    paintResults(session.standings(), winner);
    sfx.play(winner === session.selfSeat ? 'win' : 'lose');
    return;
  }

  if (match) {
    const g = match.games[0];
    const best = store.get<number>(`best:${mode.id}`, 0) ?? 0;
    const reached = match.diedOnWave[0] > 0 ? match.diedOnWave[0] : match.wave;
    if (reached > best) store.set(`best:${mode.id}`, reached);
    paintResults(match.standings(), winner);
    sfx.play(winner === 0 ? 'win' : 'lose');
    void g;
  }
}

type Row = Standing | PeerStatus;

/**
 * The results table.
 *
 * Principle #9: EVERY player's outcome, every time, with a per-player breakdown
 * of what they actually did — not a winner banner and your own score reflected
 * back at you. And the "what you missed" column is the honest one for this game:
 * with the towers you bought, a perfect mazer's corridor is computable, so every
 * player can see how much road they left on the table.
 */
function paintResults(rows: Row[], winner: number): void {
  const g = myGame();
  const selfSeat = session ? session.selfSeat : 0;
  const isSolo = !session;

  const cells = rows
    .map((r) => {
      const seat = 'seat' in r ? r.seat : 0;
      const name = r.name;
      const alive = r.alive;
      const won = seat === winner;
      const towers = 'towers' in r ? r.towers : 0;
      const peak = 'peakRoad' in r ? r.peakRoad : 0;
      const reference = g ? greedyMazeLengthCached(g.spec, mode.id, Math.max(1, towers)) : 0;
      const wonRounds = session ? (tally.get(session.players[seat]?.id ?? '') ?? 0) : 0;

      return `
        <tr class="${won ? 'won' : ''}${seat === selfSeat ? ' self' : ''}">
          <td class="r-name">
            <span class="rival-glyph glyph-${seatGlyph(seat)}" style="--c:${seatColour(seat)}"></span>
            ${esc(name)}${seat === selfSeat ? ' <span class="muted">(you)</span>' : ''}
            ${won ? '<span class="badge">winner</span>' : ''}
            ${session && wonRounds ? `<span class="muted"> · ${wonRounds} won</span>` : ''}
          </td>
          <td>${alive ? `${r.heart} left` : 'out'}</td>
          <td>wave ${'wave' in r ? r.wave : 0}</td>
          <td>${r.kills} killed</td>
          <td>${r.leaked} leaked</td>
          <td>${r.sent} sent</td>
          <td>${r.received} taken</td>
          <td class="r-road">${peak}<span class="muted"> / ${reference}</span></td>
        </tr>`;
    })
    .join('');

  screen.innerHTML = `
    <div class="results">
      <h2 class="title-sm">${
        isSolo
          ? winner === 0
            ? 'You held.'
            : 'Your Heart stopped.'
          : winner === selfSeat
            ? 'Last Heart beating — you win.'
            : winner < 0
              ? 'A dead heat.'
              : `${esc(rows.find((r) => ('seat' in r ? r.seat : 0) === winner)?.name ?? 'Someone')} wins.`
      }</h2>

      <div class="table-wrap">
        <table class="results-table">
          <thead><tr>
            <th>Player</th><th>Heart</th><th>Reached</th><th>Kills</th>
            <th>Leaks</th><th>Sent</th><th>Taken</th><th>Corridor</th>
          </tr></thead>
          <tbody>${cells}</tbody>
        </table>
      </div>
      <p class="muted road-note">
        <strong>Corridor</strong> is the longest road you ever built, against a strong reference
        maze using <em>the same number of towers</em>. Short of it is road you left on the table
        &mdash; and beating it is very much possible.
      </p>

      <div class="results-actions" id="results-actions"></div>
      <button class="btn btn-quiet results-feedback" type="button">Something wrong? Tell me</button>
    </div>`;

  screen.querySelector<HTMLElement>('.results-feedback')?.addEventListener('click', (e) =>
    (window as any).feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );

  const actions = screen.querySelector<HTMLElement>('#results-actions')!;
  if (session && rounds) {
    paintRematch(actions);
  } else {
    actions.innerHTML = `
      <button class="btn btn-primary" id="again" type="button">Play again</button>
      <button class="btn" id="menu" type="button">Menu</button>`;
    actions.querySelector('#again')?.addEventListener('click', startSolo);
    actions.querySelector('#menu')?.addEventListener('click', showMenu);
  }
}

/**
 * The rematch controls.
 *
 * A rematch NEVER touches the room: it is a vote plus a new round number inside
 * the living Net. The countdown is rendered because a silent wait is
 * indistinguishable from a hang, and "back to lobby" does not leave the room.
 */
function paintRematch(node: HTMLElement): void {
  if (!rounds || !net) return;
  const state = rounds.state();
  const secs = state.startsInMs !== null ? Math.ceil(state.startsInMs / 1000) : null;

  node.innerHTML = `
    <button class="btn btn-primary" id="again" type="button" ${state.voted ? 'aria-pressed="true"' : ''}>
      ${state.voted ? 'Ready — waiting…' : 'Play again'}
    </button>
    ${state.isHost && state.canStart ? `<button class="btn" id="force" type="button">Start now</button>` : ''}
    <button class="btn" id="to-lobby" type="button">Back to lobby</button>
    <button class="btn btn-quiet" id="menu" type="button">Leave room</button>
    <p class="rematch-status muted">
      ${esc(state.votes.length)}/${esc(state.present.length)} ready${
        secs !== null ? ` · starting in ${secs}s` : ''
      }
    </p>`;

  node.querySelector('#again')?.addEventListener('click', () => {
    if (state.voted) rounds?.unvote();
    else rounds?.vote();
    sfx.play('select');
    paintRematch(node);
  });
  node.querySelector('#force')?.addEventListener('click', () => rounds?.go());
  node.querySelector('#to-lobby')?.addEventListener('click', showLobby);
  node.querySelector('#menu')?.addEventListener('click', () => void leaveRoom().then(showMenu));
}

// ── boot ────────────────────────────────────────────────────────────────────

window.addEventListener('beforeunload', () => {
  void net?.leave();
});

// A deep-linked invite goes straight to the room; everything else starts at the
// menu. The link is consumed once — see showRoomEntry.
if (new URL(location.href).searchParams.get('room')) showRoomEntry();
else showMenu();

void playerName;
void CREEPS;
